import { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import type { ItemResponse } from '@triade/contracts';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';

export type ItemResumo = Pick<ItemResponse, 'id' | 'codigo' | 'descricao'> & Partial<Pick<ItemResponse, 'tipo'>>;

export interface ItemComboboxProps {
  /** id do botao, para o `<label htmlFor>` do formulario. */
  id?: string;
  /** Item escolhido (null = nenhum). */
  valor: ItemResumo | null;
  onSelecionar: (item: ItemResumo) => void;
  /** Resultado da busca por codigo/descricao (a busca em si e da API; aqui so se exibe). */
  itens: ItemResumo[];
  carregando?: boolean;
  /** Falha da busca (mensagem). */
  erro?: string | null;
  termo: string;
  onTermoChange: (termo: string) => void;
  disabled?: boolean;
  /** Texto do botao sem item escolhido. */
  placeholder?: string;
  /** Nome acessivel do combobox quando nao ha `<label>`. */
  ariaLabel?: string;
}

/** Combobox de item: botao com codigo + descricao e painel com busca. Presentacional (dados por props). */
export function ItemCombobox({ id, valor, onSelecionar, itens, carregando = false, erro = null, termo, onTermoChange, disabled, placeholder = 'Buscar por código ou descrição…', ariaLabel }: ItemComboboxProps) {
  const [aberto, setAberto] = useState(false);
  const listaId = useId();

  return (
    <PopoverPrimitive.Root open={aberto} onOpenChange={setAberto}>
      <PopoverPrimitive.Trigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={aberto}
          aria-controls={listaId}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          disabled={disabled}
          className="flex h-10 w-full items-center gap-2 rounded-[10px] border border-input bg-card px-3 text-left outline-none focus-visible:border-primary focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
        >
          {valor ? (
            <>
              <span className="tabular rounded-xs bg-line-soft px-1.5 py-0.5 text-caption">{valor.codigo}</span>
              <span className="min-w-0 flex-1 truncate">{valor.descricao}</span>
            </>
          ) : (
            <span className="flex-1 text-ink-disabled">{placeholder}</span>
          )}
          <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 text-ink-muted" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[320px] overflow-hidden rounded-xl border border-border bg-popover shadow-menu"
        >
          <Command shouldFilter={false} label="Buscar item">
            <CommandInput
              placeholder="Código ou descrição"
              value={termo}
              onValueChange={onTermoChange}
            />
            <CommandList id={listaId} aria-label="Itens">
              {erro ? (
                <div role="alert" className="px-3 py-4 text-small text-danger-ink">
                  Não foi possível buscar itens: {erro}
                </div>
              ) : carregando && itens.length === 0 ? (
                <div role="status" className="px-3 py-4 text-small text-ink-muted">
                  Buscando…
                </div>
              ) : (
                <>
                  <CommandEmpty>Nenhum item encontrado.</CommandEmpty>
                  {itens.map((it) => (
                    <CommandItem
                      key={it.id}
                      value={it.id}
                      onSelect={() => {
                        onSelecionar(it);
                        setAberto(false);
                      }}
                      className={cn('gap-2', valor?.id === it.id && 'bg-line-soft')}
                    >
                      <span className="tabular rounded-xs bg-line-soft px-1.5 py-0.5 text-caption">{it.codigo}</span>
                      <span className="min-w-0 flex-1 truncate">{it.descricao}</span>
                    </CommandItem>
                  ))}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
